'use client';

import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

export type MessagePriority = 'polite' | 'assertive';

export const A11yStatusMessageContext = createContext<{
  politeMessage: string;
  assertiveMessage: string;
  setStatusMessage: (statusMessage: string, priority?: MessagePriority) => void;
} | null>(null);

export const A11yStatusMessageProvider = ({
  children,
}: { children: React.ReactNode }) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const messageRefs = useRef({
    polite: { previous: '', toggle: false },
    assertive: { previous: '', toggle: false },
  });

  const handleSetStatusMessage = useCallback(
    (newMessage: string, priority: MessagePriority = 'polite') => {
      const messageRef = messageRefs.current[priority];
      const setMessage =
        priority === 'assertive' ? setAssertiveMessage : setPoliteMessage;

      if (newMessage === messageRef.previous && newMessage !== '') {
        messageRef.toggle = !messageRef.toggle;
        // live regions are a bit quirky, so we need to use invisible characters to ensure the message is announced if it's the same as the previous message.
        const invisibleChar = messageRef.toggle ? '\u200B' : '\u200C';
        setMessage(`${newMessage}${invisibleChar}`);
      } else {
        setMessage(newMessage);
        messageRef.toggle = false;
      }

      messageRef.previous = newMessage;
    },
    [],
  );

  return (
    <A11yStatusMessageContext.Provider
      value={{
        politeMessage,
        assertiveMessage,
        setStatusMessage: handleSetStatusMessage,
      }}
    >
      {children}
    </A11yStatusMessageContext.Provider>
  );
};

export const useA11yStatusMessage = () => {
  const context = useContext(A11yStatusMessageContext);

  if (!context) {
    return {
      politeMessage: '',
      assertiveMessage: '',
      setStatusMessage: () => {
        // No-op: When provider is not available no status messages will be set.
      },
    };
  }

  return context;
};
